<script setup>
import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  showConversationMenuDropdown: { type: Boolean, default: false },
  conversationMenuFilterValue: { type: String, default: '' },
  conversationMenuFilterOptions: { type: Array, default: () => [] },
  showBasicFilter: { type: Boolean, default: true },
  showInboxFilter: { type: Boolean, default: false },
  inboxFilterValue: { type: String, default: '' },
  inboxFilterOptions: { type: Array, default: () => [] },
  showMarkAllRead: { type: Boolean, default: false },
  isMarkingAllAsRead: { type: Boolean, default: false },
  markAllButtonLabel: { type: String, default: '' },
  markAllButtonTooltip: { type: String, default: '' },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'conversationMenuFilterChange',
  'inboxFilterChange',
  'markAllRead',
]);

const { uiSettings, updateUISettings } = useUISettings();
const showTitleDropdown = ref(false);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const closeTitleDropdown = () => {
  showTitleDropdown.value = false;
};

const toggleTitleDropdown = () => {
  showTitleDropdown.value = !showTitleDropdown.value;
};

const onConversationMenuFilterChange = value => {
  emit('conversationMenuFilterChange', value);
  closeTitleDropdown();
};

const activeInboxFilterLabel = computed(() => {
  return (
    props.inboxFilterOptions.find(
      option => option.value === props.inboxFilterValue
    )?.label || ''
  );
});

const onInboxFilterChange = value => {
  emit('inboxFilterChange', value);
};

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="relative z-10 flex items-center justify-between gap-2 px-3 h-12"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <div
        v-if="showConversationMenuDropdown"
        class="relative flex items-center justify-center min-w-0"
      >
        <button
          type="button"
          class="flex items-center min-w-0 gap-1 text-base font-medium text-left text-n-slate-12"
          :title="pageTitle"
          @click="toggleTitleDropdown"
        >
          <span class="truncate">{{ pageTitle }}</span>
          <span
            class="i-lucide-chevron-down size-4 shrink-0 transition-transform"
            :class="{ 'rotate-180': showTitleDropdown }"
          />
        </button>
        <div
          v-if="showTitleDropdown"
          v-on-clickaway="closeTitleDropdown"
          class="absolute left-0 z-40 min-w-[144px] p-1 mt-1 rounded-lg top-full border border-n-weak dark:border-n-strong/50 bg-n-alpha-3 backdrop-blur-[100px] shadow-lg"
          @click.stop
        >
          <button
            v-for="option in conversationMenuFilterOptions"
            :key="option.value"
            type="button"
            class="flex items-center justify-between w-full h-8 px-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-2"
            :class="{
              '!bg-n-alpha-2': option.value === conversationMenuFilterValue,
            }"
            @click="onConversationMenuFilterChange(option.value)"
          >
            <span>{{ option.label }}</span>
            <span
              v-if="option.value === conversationMenuFilterValue"
              class="i-lucide-check size-3 text-n-blue-text"
            />
          </button>
        </div>
      </div>
      <h1
        v-else
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <SelectMenu
        v-if="showInboxFilter"
        :model-value="inboxFilterValue"
        :options="inboxFilterOptions"
        :label="activeInboxFilterLabel"
        sub-menu-position="bottom"
        bottom-align="start"
        button-class="!max-w-36 shrink-0 ltr:ml-2 rtl:mr-2"
        @update:model-value="onInboxFilterChange"
      />
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders && showBasicFilter"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>
    <div class="flex items-center gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <NextButton
        v-tooltip.top-end="markAllButtonTooltip"
        data-test-id="mark-all-read"
        icon="i-lucide-check-check"
        slate
        xs
        faded
        :label="markAllButtonLabel"
        :disabled="isMarkingAllAsRead"
        @click="emit('markAllRead')"
      />
      <div class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders && showBasicFilter"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>
